<template>
  <div>
    
    <!-- page banner -->
    <page-banner
      :title="shortenName(title, airport.airport_name)"
      :subTitle="$t('payment.confirm_booking')"
      :promoBox="false"
      :payment="true"
    />

    <div class="main-area container style02">
      <div class="main-holder">
        <div class="row">
          <div class="col-lg-8 col-12">

            <!-- form -->
            <form @submit.prevent class="payment-detail">
              
              <!-- customer payment block -->
              <customer v-model="$v" />

              <!-- vehicle details payment block -->
              <vehicles v-model="$v" v-show="payment.needVehicle" />

              <!-- travel details payment block -->
              <travel v-model="$v" v-show="category !== 'airportlounge'" />

              <!-- peace of mind/ addons payment block -->
              <addons v-if="resultsAddons" />

              <!-- payment block -->
              <payment v-model="$v" @submit="hasCredit ? creditCheck() : submit()" :loading="loading" />

            </form>
          </div>
          <div class="col-lg-4 col-12">

            <!-- basket -->
            <basket v-if="!emptyCart && quote" />

          </div>
        </div>
      </div>
    </div>

    <!-- timeout modal -->
    <timeout v-if="quote" :timestamp="quote._timestamp" />

    <!-- more info -->
    <more-info />

    <!-- password -->
    <forgotten-password />

    <!-- edit payment block -->
    <edit v-if="category === 'airportparking' && editTimes" />

  </div>
</template>

<script>
import { get, sync, call } from "vuex-pathify";
import { validationMixin } from "vuelidate";
import { required, email, minLength } from "vuelidate/lib/validators";
import { PageBanner, Basket, Customer, Vehicles, Travel, Timeout, Addons, Payment, MoreInfo, ForgottenPassword, Edit } from "@/components";
import mixins from "@/mixins";

export default {
  name: 'payment-view',
  mixins: [validationMixin, mixins.format, mixins.pageview],
  components: { PageBanner, Basket, Customer, Vehicles, Travel, Timeout, Addons, Payment, MoreInfo, ForgottenPassword, Edit },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    quote: get("quote"),
    cart: get("cart/products"),
    payment: sync("payment"),
    airport: get("results/airport"),
    total: get("cart/total"),
    redirect: sync("payment/redirect"),
    title: get("cart/title"),
    emptyCart: get("cart/emptyCart"),
    paymentMethod: sync("payment/method"),
    category: get("quote/category"),
    editTimes: get("payment/editTimes"),
    resultsAddons: get("results/products@addon"),
    auth: get("auth"),
    accountCreate: sync("modals/accountCreate"),
    paymentError: sync("payment/error"),
    inProgress: sync("payment/inProgress"),
    customer() {
      return this.$store.state.customer;
    },
    vehicles() {
      return this.$store.state.payment.vehicles;
    },
    travelDetails() {
      return this.$store.state.payment.travelDetails;
    },
    hasCredit() {
      return this.auth.loggedIn && this.customer.credits > 0;
    },
    basketTotal() {
      let amount = this.customer.credits > 0 ? (this.total - this.customer.credits) : this.total;
      return parseFloat(amount > 0 ? amount : 0).toFixed(2);
    }
  },
  created() {
    this.redirect  = null;
    
    if (!this.quote || this.emptyCart) {
      window.location.href = "/";
    }
  },
  methods: {
    sendToken: call("payment/sendToken"),
    getCredits: call("customer/getCredits"),
    toggleProgress: call("payment/toggleProgress"),
    async creditCheck() {
      let { credits } = this.customer;
      
      if(this.hasCredit) {
        await this.getCredits().then(() => {
          credits === this.customer.credits || this.basketTotal == 0 ? this.submit() : this.paymentError = this.$t('payment.payment.error.wrong_try_again');
          if(this.basketTotal > 0 && this.paymentMethod === 'Manual') this.paymentMethod = 'Checkout';
        })
      } else {
        this.submit();
      }
    },
    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return this.$nextTick(() => this.focusFirstStatus());
      }
    
      // let isNewCustomer = (!this.auth.loggedIn && !this.customer.existingUser);
      // this.accountCreate = false;

      // if(passwordModal && isNewCustomer) return this.accountCreate = true;

      this.loading = true;
      this.toggleProgress(true);

      let payload = {
        "method": this.paymentMethod,
        "amount": (this.total * 100).toFixed(0),
        "currency": "GBP",
        "quoteID": this.quote.quoteID,
        "card": {
          "token": null
        },
        "domain": window.location.host
      }

      if(this.paymentMethod === 'Checkout') {
        // eslint-disable-next-line
        Frames.submitCard().then(res => {

          if(!res.token) return this.loading = false;
          payload.card.token = res.token;
          this.sendPayment(payload);
        })
      } else {
        this.sendPayment(payload);
      }
    },
    sendPayment(payload) {
      this.sendToken(payload).then(() => {
        if(this.redirect) {
          
          // remove fake vehicle on lounges payment 
          if(!this.payment.needVehicle) {
            this.vehicles.splice(-1,1);
          }

          this.$unloadScript("https://cdn.checkout.com/js/framesv2.min.js");
          window.location = this.redirect;

        } else {
          this.loading = false;
          this.toggleProgress(false);
        }
      })
    },
    focusFirstStatus() {
      let requiredElement = document.getElementsByClassName("errorInput")[0];
      const y = requiredElement.getBoundingClientRect().top + window.pageYOffset + -20;

      window.scrollTo({ top: y, behavior: 'smooth'} );
      requiredElement.focus();
    }
  },
  mounted() {

    this.toggleProgress(false)

    if(this.auth.loggedIn) {
      this.getCredits().then(() => {
        if(this.basketTotal > 0 && this.paymentMethod === 'Manual') this.paymentMethod = 'Checkout';
      });
    }
  },
  validations: {
    customer: {
      email: {
        required,
        email
      },
      first_name: {
        required,
        minLength: minLength(2)
      },
      last_name: {
        required,
        minLength: minLength(2)
      },
      mobile: {
        required
      },
      passengers: {
        $each: {
          first_name: {
            requiredPassenger(value) {
              return this.customer.passengers.length > 0 ? (value && value.length) > 0 : true
            }
          },
          last_name: {
            requiredPassenger(value) {
              return this.customer.passengers.length > 0 ? (value && value.length) > 0 : true
            }
          },
        }
      }
    },
    travelDetails: {
      return_flight_number: {
        requiredInbound(value) {
          if(this.category === 'airportparking' && this.cart.airportparking.length > 0 && this.cart.airportparking[0].product.park_type === "Chauffeur") {
            return (value && value.length) > 0;
          } else {
            return true
          }
        }
      }
    },
    vehicles: {
      minLength: minLength(1),
      $each: {
        vehicle_reg: {
          requiredVehicle(value) {
            return this.payment.needVehicle ? (value && value.length) > 0 : true
          },
          unique(value) {
            return !(this.vehicles.filter(vehicle => (vehicle.vehicle_reg.replace(/ /g,"") === value.replace(/ /g,""))).length > 1)
          }
        },
        vehicle_make: {
          requiredVehicle(value) {
            return this.payment.needVehicle ? (value && value.length) > 0 : true
          }
        },
        vehicle_model: {
          requiredVehicle(value) {
            return this.payment.needVehicle ? (value && value.length) > 0 : true
          }
        },
        vehicle_colour: {
          requiredVehicle(value) {
            return this.payment.needVehicle ? (value && value.length) > 0 : true
          }
        }
      }
    }
  }
};
</script>